import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useIsFetching } from '@tanstack/react-query'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import KNDataTableMenu from 'components/KNDataTable/KNDataTableMenu'
import KNDataTableOverflow from 'components/KNDataTable/KNDataTableOverflow'
import type { KNDataTableAction, KNDataTableColumn } from 'components/KNDataTable/types'
import { CompanyType } from 'context/authentication/Company.types'
import { Role } from 'context/authentication/Role.types'
import { getSelectedCompany } from 'context/authentication/User.helpers'
import { UserContext } from 'context/authentication/UserContext'
import { useUsersView } from 'context/UsersViewContext'
import { hasAccess, isCompanyType } from 'global/helpers/authorization'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CompanyTypeLabel from 'screens/CompanyManager/CompanyTypeLabel'
import CreateOrEditUserDialog from './CreateOrEditUserDialog'
import DeleteUserDialog from './DeleteUserDialog'
import GrantRoleDialog from './GrantRoleDialog'
import RevokeRoleDialog from './RevokeRoleDialog'
import { USERS_QUERY_KEY } from './UserManager.service'
import type { UserData } from './UserManager.types'

interface UserTableProps {
  readonly users: UserData[]
  readonly pageCount?: number
}

const UserTable = ({
  users,
  pageCount,
}: UserTableProps): ReactElement => {
  const { t } = useTranslation()
  const { page, setPage } = useUsersView()
  const { user } = useContext(UserContext)
  const [activeUser, setActiveUser] = useState<UserData>()
  const createOrEditUserDialog = useDialog<typeof CreateOrEditUserDialog>()
  const deleteUserDialog = useDialog<typeof DeleteUserDialog>()
  const grantRoleDialog = useDialog<typeof GrantRoleDialog>()
  const revokeRoleDialog = useDialog<typeof RevokeRoleDialog>()
  const isFetching = useIsFetching({ queryKey: [USERS_QUERY_KEY] })

  const handleEditClick = useCallback((row: UserData) => {
    setActiveUser(row)
    createOrEditUserDialog.handleOpen()
  }, [])

  const handleDeleteClick = useCallback((row: UserData) => {
    setActiveUser(row)
    deleteUserDialog.handleOpen()
  }, [])

  const handleGrantRoleClick = useCallback((row: UserData) => {
    setActiveUser(row)
    grantRoleDialog.handleOpen()
  }, [])

  const handleRevokeRoleClick = useCallback((row: UserData) => {
    setActiveUser(row)
    revokeRoleDialog.handleOpen()
  }, [])

  const displayNameColumnValue = (row: UserData) => (
    <Typography variant="p3" color="text.main">{row.displayName}</Typography>
  )

  const emailColumnValue = (row: UserData) => (
    <Typography variant="p3" color="text.main">{row.email}</Typography>
  )

  const companiesColumnValue = (row: UserData) => (
    <Stack spacing={1} direction="row" alignItems="center">
      {row.companies.length > 0 ? (
        <KNDataTableOverflow
          row={row}
          label={t('general.view_all')}
          header={t('screens.shared.user_manager.card.columns.companies')}
          items={row.companies.map((company) => (
            <Stack
              key={company.cid}
              spacing={1}
              direction="row"
              alignItems="center"
            >
              <CompanyTypeLabel type={company.type} compact />
              <Box>{company.displayName}</Box>
              <Typography variant="p6" color="primary.light">
                {t(`screens.shared.user_manager.roles.${row.roles[company.cid]}`)}
              </Typography>
            </Stack>
          ))}
          rightAligned
        />
      ) : (
        <Typography variant="p3" color="primary.light">{t('general.none')}</Typography>
      )}
      {hasAccess(Role.Admin) && (
        <KNDataTableMenu
          label={t('screens.shared.user_manager.card.actions.role_options')}
          row={row}
          actions={[
            {
              name: 'grant_role',
              label: t('screens.shared.user_manager.grant_role.grant_role'),
              onClick: handleGrantRoleClick,
            },
            ...(row.companies.length > 0 ? [
              {
                name: 'revoke_role',
                label: t('screens.shared.user_manager.revoke_role.revoke_role'),
                onClick: handleRevokeRoleClick,
              },
            ] : []),
          ]}
        />
      )}
    </Stack>
  )

  const roleColumnValue = (row: UserData) => {
    const selectedCompany = getSelectedCompany()
    if (selectedCompany) {
      return t(`screens.shared.user_manager.roles.${row.roles[selectedCompany.cid]!}`)
    }
    return (
      <Typography color="primary.light">{t('general.none')}</Typography>
    )
  }

  const columns = useMemo((): KNDataTableColumn<UserData>[] => [
    {
      name: 'displayName',
      label: t('screens.shared.user_manager.card.columns.display_name'),
      getValue: displayNameColumnValue,
      sx: { width: { xs: '18rem', xxl: '24rem' } },
    },
    {
      name: 'email',
      label: t('screens.shared.user_manager.card.columns.email'),
      getValue: emailColumnValue,
      sx: { width: { xs: '18rem', xxl: '24rem' } },
    },
    ...(isCompanyType(CompanyType.Operator) ? [
      {
        name: 'companies',
        label: t('screens.shared.user_manager.card.columns.companies'),
        getValue: companiesColumnValue,
        sx: { width: { xs: '24rem', sm: 'auto' } },
      },
    ] : [
      {
        name: 'role',
        label: t('screens.shared.user_manager.card.columns.role'),
        getValue: roleColumnValue,
        sx: { width: { xs: '24rem', sm: 'auto' } },
      },
    ]),
  ], [])

  const actions = useMemo((): KNDataTableAction<UserData>[] => [
    {
      name: 'edit',
      label: t('screens.shared.user_manager.card.actions.edit'),
      icon: <EditIcon />,
      onClick: handleEditClick,
    },
    {
      name: 'delete',
      label: t('screens.shared.user_manager.card.actions.delete'),
      icon: <DeleteIcon />,
      onClick: handleDeleteClick,
      isVisible: (row: UserData) => row.email !== user?.email,
    },
  ], [])

  return (
    <>
      <KNDataTable
        columns={columns}
        actions={hasAccess(Role.Admin) ? actions : []}
        data={users}
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
        isLoading={isFetching > 0}
        sx={{
          marginX: -2,
        }}
      />
      {activeUser && hasAccess(Role.Admin) && (
        <>
          <CreateOrEditUserDialog
            payload={{
              user: activeUser,
            }}
            open={createOrEditUserDialog.isOpen}
            onAction={createOrEditUserDialog.handleAction}
            onClose={createOrEditUserDialog.handleClose}
          />
          <DeleteUserDialog
            payload={{
              user: activeUser,
            }}
            open={deleteUserDialog.isOpen}
            onAction={deleteUserDialog.handleAction}
            onClose={deleteUserDialog.handleClose}
          />
          <GrantRoleDialog
            payload={{
              user: activeUser,
            }}
            open={grantRoleDialog.isOpen}
            onAction={grantRoleDialog.handleAction}
            onClose={grantRoleDialog.handleClose}
          />
          <RevokeRoleDialog
            payload={{
              user: activeUser,
            }}
            open={revokeRoleDialog.isOpen}
            onAction={revokeRoleDialog.handleAction}
            onClose={revokeRoleDialog.handleClose}
          />
        </>
      )}
    </>
  )
}

export default UserTable
